import { useMemo, useState } from 'react'
import { CalendarSearch, Check, ChevronsUpDown, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useCalendarEventsQuery } from '@/features/calendar/api/useCalendarEventsQuery'
import type { ICalendarEvent } from '@/features/calendar/interfaces'

export type CpdEventOption = {
  id: string
  name: string
  source?: ICalendarEvent
}

export interface CpdEventSelectProps {
  value?: string | null
  onChange: (option: CpdEventOption | null) => void
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  emptyLabel?: string
}

export function CpdEventSelect({
  value,
  onChange,
  placeholder = 'Search CPD events…',
  disabled = false,
  allowClear = true,
  emptyLabel = 'No CPD events found.',
}: CpdEventSelectProps) {
  const [open, setOpen] = useState(false)

  const eventsQuery = useCalendarEventsQuery()
  const events = eventsQuery.data ?? []

  const options = useMemo(() => {
    return events.map((event) => {
      const id = event.sourceId != null ? String(event.sourceId) : event.id
      const name = event.title ?? 'Untitled CPD event'
      const date = event.startAt ? new Date(event.startAt) : null
      const dateLabel = date ? date.toLocaleDateString() : undefined
      return {
        id,
        name,
        searchValue: `${name} ${id} ${event.theme ?? ''} ${dateLabel ?? ''}`.toLowerCase(),
        subtitle: dateLabel ? `Starts ${dateLabel}` : undefined,
        source: event,
      }
    })
  }, [events])

  const selectedOption = value
    ? options.find((option) => option.id === value)
    : undefined

  const handleSelect = (optionId: string) => {
    const nextOption = options.find((option) => option.id === optionId)
    if (!nextOption) return
    onChange({ id: nextOption.id, name: nextOption.name, source: nextOption.source })
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setOpen(false)
  }

  const isLoading = eventsQuery.isLoading || eventsQuery.isFetching

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          <span className="flex items-center gap-2 truncate">
            <CalendarSearch className="h-4 w-4 shrink-0 text-muted-foreground" />
            {isLoading
              ? 'Loading CPD events…'
              : selectedOption
                ? selectedOption.name
                : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command className="[&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]]:py-2 [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-border [&_[cmdk-input]]:text-sm [&_[cmdk-input]]:placeholder:text-muted-foreground">
          <CommandInput placeholder="Search CPD events…" className="text-sm" />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading CPD events…
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyLabel}</CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={option.searchValue}
                      onSelect={() => handleSelect(option.id)}
                    >
                      <div className="flex flex-col">
                        <span className="flex items-center gap-2 text-sm">
                          <Check
                            className={cn(
                              'h-4 w-4',
                              value === option.id ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {option.name}
                        </span>
                        {option.subtitle && (
                          <span className="pl-6 text-xs text-muted-foreground">
                            {option.subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                {allowClear && value ? (
                  <CommandGroup>
                    <CommandItem
                      value="__clear__"
                      onSelect={handleClear}
                      className="text-muted-foreground"
                    >
                      Clear selection
                    </CommandItem>
                  </CommandGroup>
                ) : null}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
